import { Component, lazy, Suspense, type ReactNode } from "react";
import { PhoneOff } from "lucide-react";

import { useAccount, type CallingFlags } from "@/providers/AccountProvider";

// Where AppShell mounts calling -- the dock once, outside <main> and outside the routed page's
// boundary, and the phone launcher in the icon rail (docs/vault/Plans/WhatsApp-Calling.md §11).
//
// Both are their own chunks, so a workspace without calling never downloads the softphone. Each
// gets its OWN Suspense boundary on purpose: AppShell has none, and the nearest one is App.tsx's,
// around every route, whose fallback is the whole-app skeleton. Without these, a chunk suspending
// on an ordinary update -- the gate turning true after identity loads -- would swap the entire
// app, page included, for that skeleton. And each its own error boundary: a crash costs only
// that piece, never the page, the rail or the other piece.

const CallDock = lazy(() => import("@/calls/CallDock"));
const CallLauncher = lazy(() => import("@/calls/CallLauncher"));
const CallCustomerButton = lazy(() => import("@/calls/CallCustomerButton"));

const CALLS_FAILED = "Calls stopped working. Reload the page to take calls again.";

/**
 * v2: outgoing calls are on here, for a real seat granted calls.dial AND calls.answer -- a dialled
 * call needs the feed, the hang-up and the alive ping, which are calls.answer routes on the
 * server. The server checks again.
 */
export function dialAllowed(calling: CallingFlags, can: (permission: string) => boolean): boolean {
  return calling.platform && calling.seat && calling.dial === true && can("calls.dial") && can("calls.answer");
}

/**
 * §10: only where calling is live, to a real seat that may answer -- or, since v2, dial: a dialled
 * call needs the panel too. The server checks again.
 */
export function dockAllowed(calling: CallingFlags, can: (permission: string) => boolean): boolean {
  const answers = calling.platform && calling.enabled && calling.seat && can("calls.answer");
  return answers || dialAllowed(calling, can);
}

interface CallsErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface CallsErrorBoundaryState {
  failed: boolean;
}

class CallsErrorBoundary extends Component<CallsErrorBoundaryProps, CallsErrorBoundaryState> {
  override state: CallsErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): CallsErrorBoundaryState {
    return { failed: true };
  }

  override render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

const DOCK_FAILED = (
  <p
    role="alert"
    className="fixed bottom-4 right-4 z-40 max-w-[calc(100vw-2rem)] rounded-[var(--radius-ctl)] border-hairline border-brand-border bg-brand-surface px-3 py-2 text-[12px] text-n-fg-muted"
  >
    {CALLS_FAILED}
  </p>
);

const LAUNCHER_FAILED = (
  <span
    role="img"
    aria-label={CALLS_FAILED}
    title={CALLS_FAILED}
    className="flex size-10 items-center justify-center text-n-critical"
  >
    <PhoneOff size={18} aria-hidden="true" />
  </span>
);

export function CallDockRoot() {
  const { calling, can } = useAccount();
  if (!dockAllowed(calling, can)) return null;
  return (
    <CallsErrorBoundary fallback={DOCK_FAILED}>
      <Suspense fallback={null}>
        <CallDock />
      </Suspense>
    </CallsErrorBoundary>
  );
}

/** The phone launcher, for the foot of the icon rail. Same gate as the dock. */
export function CallLauncherSlot() {
  const { calling, can } = useAccount();
  if (!dockAllowed(calling, can)) return null;
  return (
    <div className="flex justify-center pb-3">
      <CallsErrorBoundary fallback={LAUNCHER_FAILED}>
        {/* The button's own size while its chunk loads, so the rail does not shift. */}
        <Suspense fallback={<span className="size-10" aria-hidden="true" />}>
          <CallLauncher />
        </Suspense>
      </CallsErrorBoundary>
    </div>
  );
}

/** The conversation the header's phone icon calls: a WhatsApp thread's number and customer. */
export interface CallableThread {
  channel: string;
  account_id: string;
  contact_id: string;
  contact_name?: string | null;
}

/**
 * The phone icon for a conversation's header (v2). Only a WhatsApp chat, and only for a seat
 * that may dial; its own chunk, so the inbox never downloads the softphone where nobody dials.
 */
export function CallCustomerSlot({ thread }: { thread: CallableThread }) {
  const { calling, can } = useAccount();
  if (thread.channel !== "whatsapp" || !dialAllowed(calling, can)) return null;
  return (
    <CallsErrorBoundary fallback={null}>
      <Suspense fallback={<span className="size-8 shrink-0" aria-hidden="true" />}>
        {/* Keyed on the conversation: another customer gets a fresh popover, never the last one's
            permission or an answer still in flight. */}
        <CallCustomerButton
          key={`${thread.account_id}:${thread.contact_id}`}
          phoneNumberId={thread.account_id}
          contact={thread.contact_id}
          name={thread.contact_name ?? null}
        />
      </Suspense>
    </CallsErrorBoundary>
  );
}
